/**
 * Copyright (C) 2017 HERE Global B.V. and its affiliate(s).
 * All rights reserved.
 *
 * This software and other materials contain proprietary information
 * controlled by HERE and are protected by applicable copyright legislation.
 * Any use and utilization of this software and other materials and
 * disclosure to any third parties is conditional upon having a separate
 * agreement with HERE for the access, use, utilization or disclosure of this
 * software. In the absence of such agreement, the use of the software is not
 * allowed.
 */

import LuiBase from '@here/lui-base'
import scopedCss from 'lui-textfield.scss'
import template from 'lui-textfield.html'

export default class LuiTextfield extends LuiBase {
  static get is() {
    return 'lui-textfield'
  }

  static get attributeToggles() {
    return [
      'autocomplete',
      'autofocus',
      'autoresize',
      'disabled',
      'inputmode',
      'list',
      'max',
      'maxlength',
      'maxrows',
      'min',
      'minlength',
      'name',
      'pattern',
      'placeholder',
      'readonly',
      'required',
      'right',
      'size',
      'spellcheck',
      'step',
      'tabindex',
      'type',
      'cols',
      'rows',
      'wrap',
      'autocapitalize',
      'autocorrect',
    ]
  }

  static get css() {
    return scopedCss
  }

  static get template() {
    return template
  }

  static get hasLuiElementsInShadowDom() {
    return false
  }

  get lineHeight() {
    this._lineHeight = this._lineHeight || Number.parseInt(window.getComputedStyle(this.field).lineHeight)
    return this._lineHeight
  }

  get field() {
    this._field = this._field || this._getField()
    return this._field
  }

  get wrapper() {
    return this._wrapper || (this._wrapper = this.shadowRoot.querySelector('.lui-textfield__wrapper'))
  }

  get value() {
    return this.field ? this.field.value : this.getAttribute('value')
  }

  set value(newValue) {
    this.setAttribute('value', newValue)
    this.valueChanged('', newValue)
  }

  autoresizeChanged(oldVal, newVal) {
    const action = newVal === '' ? 'addEventListener' : 'removeEventListener'
    this._autoresizeBound = this._autoresizeBound || (() => this._autoresize())
    this.field[action]('keyup', this._autoresizeBound)
    this.field[action]('input', this._autoresizeBound)
  }

  /**
   * The event is fired after the LUI Textfield was cleard.
   *
   * @fires lui-textfield:cleared
   */
  clearField() {
    this.field.value = ''
    this.field.focus()
    this.dispatchEvent(new CustomEvent('lui-textfield:cleared', {bubbles: true, composed: true}))
  }

  disabledChanged() {
    this.toggleCssClass('lui-disabled', this.hasAttribute('disabled'))
  }

  init() {
    super.init()

    this._onFocus = () => {
      if ((this.tabIndex !== -1 || this.field.tabIndex !== -1) && !this.disabled) {
        this.classList.add('lui-focus')
      }
    }

    this._onBlur = () => {
      this.classList.remove('lui-focus')
      this.setAttribute('value', this.field.value)
    }
  }

  multilineChanged() {
    this.toggleCssClass('lui-textfield--multiline', this.hasAttribute('multiline'))
  }

  onConnected() {
    super.onConnected()
    const types = ['text', 'number', 'email', 'password', 'tel', 'url']
    if (types.indexOf(this.getAttribute('type')) === -1) {
      this.setAttribute('type', 'text')
    }
    this.delay(() => {
      this.field.value = this.field.value || this.getAttribute('value')
      this.field.addEventListener('focus', this._onFocus)
      this.field.addEventListener('blur', this._onBlur)

      this._checkAutofocus()
    })
  }

  onDisconnected() {
    super.onDisconnected()
    this.field.removeEventListener('focus', this._onFocus)
    this.field.removeEventListener('blur', this._onBlur)
    if (this.hasAttribute('autoresize')) {
      this.field.removeEventListener('keyup', this._autoresizeBound)
      this.field.removeEventListener('input', this._autoresizeBound)
    }
  }

  readonlyChanged() {
    this.toggleCssClass('lui-readonly', this.hasAttribute('readonly'))
  }

  valueChanged(oldVal, newVal) {
    this._valueChanged(oldVal, newVal)
    this._updateInputField()
  }

  _autoresize() {
    const maxRows = Number.parseInt(this.field.getAttribute('maxrows'))
    const scrollHeight = this.field.scrollHeight
    const style = window.getComputedStyle(this.field)
    const paddingY = Number.parseInt(style.paddingTop) + Number.parseInt(style.paddingBottom)
    const currentRows = (scrollHeight - paddingY) / this.lineHeight
    this.field.rows = maxRows && currentRows > maxRows ? maxRows : currentRows
  }

  _checkAutofocus() {
    if (this.getAttribute('autofocus') !== null && document.activeElement === this) {
      this._onFocus()
    }
  }

  _createField() {
    const tagName = this.hasAttribute('multiline') ? 'textarea' : 'input'
    const element = document.createElement(tagName)
    const defaultSlot = this.shadowRoot.querySelector('slot:not([name])')
    this.wrapper.insertBefore(element, defaultSlot)
    return element
  }

  _getField() {
    const field = this.querySelector('input, textarea') || this._createField()
    const multiline = field.tagName === 'TEXTAREA'
    this.constructor.attributeToggles.concat('value').forEach((attributeName) => {
      const attributeValue = this.getAttribute(attributeName)

      if (attributeValue !== null && !(attributeName === 'type' && multiline)) {
        if (typeof field[attributeName] !== 'undefined') {
          // Prefer to set in JS if possible; convert values for JS if needed:
          // TODO LUI-1045: Extract React specific behavior (unnecessary check for 'false' value)
          field[attributeName] = LuiBase.isBooleanAttribute(attributeName) ? (attributeValue !== 'false') : attributeValue
        } else {
          // Fallback if attribute has no JS equivalent:
          field.setAttribute(attributeName, attributeValue)
        }
      }
    })
    this.seedElement = field
    return field
  }

  _updateInputField() {
    const inputField = this.querySelector('input')
    if (inputField) {
      inputField.value = this.field.value
    }
  }

  _valueChanged(oldVal, newVal) {
    if (this._initialized) {
      this.field.value = newVal
    }
  }
}



//////////////////
// WEBPACK FOOTER
// ./packages/lui-textfield/lui-textfield.js
// module id = 13
// module chunks = 0 1 8 22